import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getCourseByIdOptions,
	updateCourseMutation,
	unenrollStudentMutation,
	cancelUnenrollStudentMutation,
	getAllCoursesOptions,
	moveStudentMutation,
	getAllCoursesQueryKey,
	getCourseByIdQueryKey,
	addExcludedDateMutation,
	removeExcludedDateMutation,
	getAllTeachersOptions,
	archiveCourseMutation,
	unarchiveCourseMutation,
} from "@/lib/client/@tanstack/react-query.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
	ArrowLeft,
	Edit,
	Save,
	X,
	Clock,
	Euro,
	Users,
	Calendar,
	UserMinus,
	CheckCircle2,
	XCircle,
	AlertCircle,
	Eye,
	ArrowRightLeft,
	MessageSquare,
	User,
	Archive,
	ArchiveRestore,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";
import {
	getSubjectLabel,
	getLevelLabel,
	getSchoolLabel,
	getTimeSlot,
	enumStore,
} from "@/stores/enum-store";
import { UnenrollDialog } from "@/components/UnenrollDialog";
import { ArchiveCourseDialog } from "@/components/ArchiveCourseDialog";

export const Route = createFileRoute("/admin/courses/$id")({
	component: CourseDetailsPage,
});

function CourseDetailsPage() {
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [isEditing, setIsEditing] = useState(false);
	const [moveDialogOpen, setMoveDialogOpen] = useState(false);
	const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
	const [existingUnenrollDate, setExistingUnenrollDate] = useState<string | undefined>(undefined);
	const [selectedStudent, setSelectedStudent] = useState<any>(null);
	const [targetCourseId, setTargetCourseId] = useState("");
	const [newExcludedDate, setNewExcludedDate] = useState("");
	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

	const { data: course, isLoading } = useQuery({
		...getCourseByIdOptions({ path: { id } }),
	});
	const isArchived = !!course?.archivedAt;

	const { data: allCourses } = useQuery(getAllCoursesOptions());
	const { data: teachersData } = useQuery(getAllTeachersOptions());
	const teachers = teachersData || [];

	const timeSlot = useMemo(() => {
		return course ? getTimeSlot(course.timeSlot!) : null;
	}, [course]);

	const updateCourseMut = useMutation({
		...updateCourseMutation(),
		onSuccess: () => {
			toast.success("Cours modifié avec succès");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
			setIsEditing(false);
		},
		onError: () => {
			toast.error("Erreur lors de la modification du cours");
		},
	});

	const unenrollStudentMut = useMutation({
		...unenrollStudentMutation(),
		onSuccess: () => {
			toast.success("Élève désinscrit du cours");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
		},
		onError: () => {
			toast.error("Erreur lors de la désinscription");
		},
	});

	const cancelUnenrollMut = useMutation({
		...cancelUnenrollStudentMutation(),
		onSuccess: () => {
			toast.success("Désinscription annulée");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
			setUnenrollDialogOpen(false);
			setSelectedStudent(null);
			setExistingUnenrollDate(undefined);
		},
		onError: () => {
			toast.error("Erreur lors de l'annulation de la désinscription");
		},
	});

	const moveStudentMut = useMutation({
		...moveStudentMutation(),
		onSuccess: () => {
			toast.success("Élève changé de cours avec succès");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
			setMoveDialogOpen(false);
			setSelectedStudent(null);
			setTargetCourseId("");
		},
		onError: () => {
			toast.error("Erreur lors du changement de cours");
		},
	});

	const addExcludedDateMut = useMutation({
		...addExcludedDateMutation(),
		onSuccess: () => {
			toast.success("Date d'exclusion ajoutée");
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
			setNewExcludedDate("");
		},
		onError: () => {
			toast.error("Erreur lors de l'ajout de la date");
		},
	});

	const removeExcludedDateMut = useMutation({
		...removeExcludedDateMutation(),
		onSuccess: () => {
			toast.success("Date d'exclusion supprimée");
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
		},
		onError: () => {
			toast.error("Erreur lors de la suppression de la date");
		},
	});

	const archiveCourseMut = useMutation({
		...archiveCourseMutation(),
		onSuccess: () => {
			toast.success("Cours archivé : inscriptions clôturées et présences conservées");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
			setArchiveDialogOpen(false);
			setIsEditing(false);
		},
		onError: () => {
			toast.error("Erreur lors de l'archivage du cours");
		},
	});

	const unarchiveCourseMut = useMutation({
		...unarchiveCourseMutation(),
		onSuccess: () => {
			toast.success("Cours désarchivé");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getCourseByIdQueryKey({path: {id}}) });
		},
		onError: () => {
			toast.error("Erreur lors du désarchivage du cours");
		},
	});

	const form = useForm({
		defaultValues: {
			subject: course?.subject || "",
			level: course?.level || "",
			school: course?.school || "",
			timeSlot: course?.timeSlot,
			pricePerSession: course?.pricePerSession || 25,
			startDate: course?.startDate || "",
			teacherId: course?.teacherId || "",
		},
		onSubmit: async ({ value }) => {
			updateCourseMut.mutate({
				path: { id },
				body: {
					subject: value.subject,
					level: value.level,
					school: value.school,
					pricePerSession: value.pricePerSession,
					startDate: value.startDate || undefined,
					timeSlot: value.timeSlot as any,
					teacherId: value.teacherId || undefined,
				},
			});
		},
	});

	const handleOpenUnenrollDialog = (student: any, unrolledDate?: string) => {
		setSelectedStudent(student);
		setExistingUnenrollDate(unrolledDate || undefined);
		setUnenrollDialogOpen(true);
	};

	const handleCancelUnenroll = () => {
		if (!selectedStudent) return;
		cancelUnenrollMut.mutate({
			path: {
				courseId: id,
				studentId: selectedStudent.id,
			},
		});
	};

	const handleUnenrollConfirm = (unenrollDate: string) => {
		if (!selectedStudent) return;
		unenrollStudentMut.mutate({
			path: {
				courseId: id,
				studentId: selectedStudent.id,
			},
			body: {
				unenrollDate,
			},
		});
		setUnenrollDialogOpen(false);
		setSelectedStudent(null);
		setExistingUnenrollDate(undefined);
	};

	const handleOpenMoveDialog = (student: any) => {
		setSelectedStudent(student);
		setMoveDialogOpen(true);
	};

	const handleMoveStudent = () => {
		if (!selectedStudent || !targetCourseId) {
			toast.error("Veuillez sélectionner un cours de destination");
			return;
		}

		moveStudentMut.mutate({
			path: {
				fromCourseId: id,
				studentId: selectedStudent.id,
				toCourseId: targetCourseId,
			},
			body: {
				enrollmentDate: new Date().toISOString().split("T")[0],
			},
		});
	};

	const dayOfWeekLabels: Record<string, string> = {
		MONDAY: "Lundi",
		TUESDAY: "Mardi",
		WEDNESDAY: "Mercredi",
		THURSDAY: "Jeudi",
		FRIDAY: "Vendredi",
		SATURDAY: "Samedi",
		SUNDAY: "Dimanche",
	};

	const attendanceStatusLabels: Record<
		string,
		{ label: string; icon: any; color: string }
	> = {
		PRESENT: { label: "Présent", icon: CheckCircle2, color: "text-green-600" },
		ABSENT: { label: "Absent", icon: XCircle, color: "text-red-600" },
		EXCUSED: { label: "Excusé", icon: AlertCircle, color: "text-orange-600" },
	};

	if (isLoading || !course) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement du cours...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate({ to: "/admin/courses" })}
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Retour
				</Button>
			</div>

			<div className="flex justify-between items-start">
				<div>
					<div className="flex items-center gap-3">
						<h1 className="text-3xl font-bold">
							{getSubjectLabel(course.subject)} - {getLevelLabel(course.level)}
						</h1>
						{isArchived && (
							<Badge variant="outline" className="text-gray-600 border-gray-300 bg-gray-100">
								<Archive className="h-3 w-3 mr-1" />
								Archivé
							</Badge>
						)}
					</div>
					<p className="text-gray-600 mt-2">{getSchoolLabel(course.school)}</p>
				</div>
				{isArchived ? (
					<div className="flex gap-2">
						<Button
							variant="outline"
							onClick={() => unarchiveCourseMut.mutate({ path: { id } })}
							disabled={unarchiveCourseMut.isPending}
						>
							<ArchiveRestore className="h-4 w-4 mr-2" />
							{unarchiveCourseMut.isPending ? "Désarchivage..." : "Désarchiver"}
						</Button>
					</div>
				) : !isEditing ? (
					<div className="flex gap-2">
						<Link to="/admin/sms" search={{ courseId: id, parentIds: [] }}>
							<Button variant="outline" size="sm">
								<MessageSquare className="h-4 w-4 mr-2" />
								Envoyer SMS aux parents
							</Button>
						</Link>
						<Button
							variant="outline"
							onClick={() => setArchiveDialogOpen(true)}
							disabled={archiveCourseMut.isPending}
						>
							<Archive className="h-4 w-4 mr-2" />
							Archiver
						</Button>
						<Button onClick={() => setIsEditing(true)}>
							<Edit className="h-4 w-4 mr-2" />
							Modifier
						</Button>
					</div>
				) : (
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => setIsEditing(false)}>
							<X className="h-4 w-4 mr-2" />
							Annuler
						</Button>
						<Button
							onClick={() => form.handleSubmit()}
							disabled={updateCourseMut.isPending}
						>
							<Save className="h-4 w-4 mr-2" />
							{updateCourseMut.isPending ? "Enregistrement..." : "Enregistrer"}
						</Button>
					</div>
				)}
			</div>

			{isArchived && course.archivedAt && (
				<div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
					<Archive className="h-5 w-5 text-gray-500 mt-0.5 shrink-0" />
					<div className="text-sm text-gray-700">
						<p className="font-medium">
							Cours archivé le{" "}
							{new Date(course.archivedAt).toLocaleDateString("fr-FR")}
						</p>
						<p className="text-gray-600 mt-0.5">
							Ce cours est en lecture seule. Les inscriptions ont été clôturées
							et les présences sont conservées à titre d'historique. Désarchivez
							le cours pour le modifier à nouveau.
						</p>
					</div>
				</div>
			)}

			<Card>
				<CardHeader>
					<CardTitle>Informations du cours</CardTitle>
					<CardDescription>Détails et horaires du cours</CardDescription>
				</CardHeader>
				<CardContent>
					{!isEditing ? (
						<div className="grid grid-cols-2 gap-6">
							<div className="space-y-4">
								<div>
									<Label className="text-gray-500">Matière</Label>
									<p className="font-medium">
										{getSubjectLabel(course.subject)}
									</p>
								</div>
								<div>
									<Label className="text-gray-500">Niveau</Label>
									<p className="font-medium">{getLevelLabel(course.level)}</p>
								</div>
								<div>
									<Label className="text-gray-500">Établissement</Label>
									<p className="font-medium">{getSchoolLabel(course.school)}</p>
								</div>
								<div>
									<Label className="text-gray-500">Professeur</Label>
									<div className="flex items-center gap-2">
										<User className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{course.teacherName || "Non assigné"}
										</p>
									</div>
								</div>
							</div>
							<div className="space-y-4">
								<div>
									<Label className="text-gray-500">Jour</Label>
									<div className="flex items-center gap-2">
										<Calendar className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{dayOfWeekLabels[timeSlot?.dayOfWeek || ""]}
										</p>
									</div>
								</div>
								<div>
									<Label className="text-gray-500">Horaires</Label>
									<div className="flex items-center gap-2">
										<Clock className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{timeSlot?.startTime} - {timeSlot?.endTime}
										</p>
									</div>
								</div>
								<div>
									<Label className="text-gray-500">Date de début</Label>
									<div className="flex items-center gap-2">
										<Calendar className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{course.startDate
												? new Date(course.startDate).toLocaleDateString("fr-FR")
												: "Cours en pause"}
										</p>
									</div>
								</div>
								<div>
									<Label className="text-gray-500">Prix par séance</Label>
									<div className="flex items-center gap-2">
										<Euro className="h-4 w-4 text-green-600" />
										<p className="font-medium">
											{course.pricePerSession?.toFixed(2)} €
										</p>
									</div>
								</div>
							</div>
						</div>
					) : (
						<form className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<form.Field
									name="subject"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Matière</Label>
											<Select
												value={field.state.value}
												onValueChange={(value) => field.handleChange(value)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner une matière" />
												</SelectTrigger>
												<SelectContent>
													{enumStore.state.subjects.map((subject) => (
														<SelectItem
															key={subject.value}
															value={subject.value}
														>
															{subject.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
								/>
								<form.Field
									name="level"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Niveau</Label>
											<Select
												value={field.state.value}
												onValueChange={(value) => field.handleChange(value)}
											>
												<SelectTrigger>
													<SelectValue placeholder="Sélectionner un niveau" />
												</SelectTrigger>
												<SelectContent>
													{enumStore.state.levels.map((level) => (
														<SelectItem key={level.value} value={level.value}>
															{level.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									)}
								/>
							</div>

							<form.Field
								name="school"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Établissement</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) => field.handleChange(value)}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un établissement" />
											</SelectTrigger>
											<SelectContent>
												{enumStore.state.schools.map((school) => (
													<SelectItem key={school.value} value={school.value}>
														{school.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="teacherId"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Professeur</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) => field.handleChange(value)}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un professeur" />
											</SelectTrigger>
											<SelectContent>
												{teachers.map((teacher) => (
													<SelectItem key={teacher.id} value={teacher.id}>
														{teacher.fullName}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="timeSlot"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Créneau</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) =>
												field.handleChange(value as any)
											}
										>
											<SelectTrigger>
												<SelectValue placeholder="Sélectionner un créneau" />
											</SelectTrigger>
											<SelectContent>
												{enumStore.state.timeSlots.map((slot) => (
													<SelectItem key={slot.value} value={slot.value}>
														{slot.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="pricePerSession"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Prix par séance (€)</Label>
										<Input
											id={field.name}
											type="number"
											min={0}
											step="0.01"
											value={field.state.value}
											onChange={(e) =>
												field.handleChange(parseFloat(e.target.value) || 0)
											}
										/>
									</div>
								)}
							/>

							<form.Field
								name="startDate"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>
											Date de début du cours (optionnel)
										</Label>
										<Input
											id={field.name}
											type="date"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
										/>
										<p className="text-xs text-gray-500 mt-1">
											Laissez vide si le cours est en pause
										</p>
									</div>
								)}
							/>
						</form>
					)}
				</CardContent>
			</Card>

			{/* Students enrolled */}
			<Card>
				<CardHeader>
					<CardTitle>
						{isArchived ? "Élèves inscrits lors de l'archivage" : "Élèves inscrits"}{" "}
						({course.enrollments?.length || 0})
					</CardTitle>
					<CardDescription>
						{isArchived
							? "Historique des inscriptions à ce cours (lecture seule)"
							: "Liste des élèves participant à ce cours"}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{course.enrollments && course.enrollments.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Élève</TableHead>
									<TableHead>École</TableHead>
									<TableHead>Date d'inscription</TableHead>
									<TableHead>Besoins spécifiques</TableHead>
									<TableHead>Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{course.enrollments
									.filter((d) => d.student)
									.map((enrolment) => (
										<TableRow key={enrolment.student!.id}>
											<TableCell>
												<div className="font-medium">
													{enrolment.student!.firstName}{" "}
													{enrolment.student!.lastName}
												</div>
											</TableCell>
											<TableCell className="text-sm text-gray-600">
												{enrolment.student!.school}
											</TableCell>
											<TableCell className="text-sm text-gray-600">
												<div className="space-y-1">
													<span>
														{enrolment.enrollmentDate
															? new Date(
																	enrolment.enrollmentDate,
																).toLocaleDateString("fr-FR")
															: "-"}
													</span>
													{enrolment.unrolledDate && (
														<div>
															<Badge variant="outline" className="text-xs text-orange-600 border-orange-300 bg-orange-50">
																Désinscription le {new Date(enrolment.unrolledDate).toLocaleDateString("fr-FR")}
															</Badge>
														</div>
													)}
												</div>
											</TableCell>
											<TableCell>
												<div className="flex gap-1">
													{enrolment.student!.hasPap && (
														<Badge variant="secondary" className="text-xs">
															PAP
														</Badge>
													)}
													{enrolment.student!.hasPps && (
														<Badge variant="secondary" className="text-xs">
															PPS
														</Badge>
													)}
													{!enrolment.student!.hasPap &&
														!enrolment.student!.hasPps && (
															<span className="text-gray-400 text-sm">-</span>
														)}
												</div>
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-2">
													<Link
														to="/admin/students/$id"
														params={{ id: enrolment.student!.id }}
													>
														<Button
															size="sm"
															variant="outline"
															title="Voir l'élève"
														>
															<Eye className="h-3 w-3" />
														</Button>
													</Link>
													{!isArchived && (
														<>
															<Button
																size="sm"
																variant="outline"
																onClick={() =>
																	handleOpenMoveDialog(enrolment.student!)
																}
																title="Changer de cours"
															>
																<ArrowRightLeft className="h-3 w-3" />
															</Button>
															<Button
																size="sm"
																variant="ghost"
																onClick={() =>
																	handleOpenUnenrollDialog(enrolment.student!, enrolment.unrolledDate)
																}
																disabled={unenrollStudentMut.isPending}
																title={enrolment.unrolledDate ? "Modifier la désinscription" : "Désinscrire"}
															>
																<UserMinus className={`h-3 w-3 ${enrolment.unrolledDate ? "text-orange-500" : ""}`} />
															</Button>
														</>
													)}
												</div>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-12">
							<Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
							<p className="text-gray-500 font-medium">Aucun élève inscrit</p>
							<p className="text-gray-400 text-sm mt-1">
								Les élèves peuvent être inscrits depuis la page de gestion des
								élèves
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Attendances */}
			<Card>
				<CardHeader>
					<CardTitle>Présences / Absences</CardTitle>
					<CardDescription>
						Historique des présences pour ce cours
					</CardDescription>
				</CardHeader>
				<CardContent>
					{course.attendances && course.attendances.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Date</TableHead>
									<TableHead>Élève</TableHead>
									<TableHead>Statut</TableHead>
									<TableHead>Notes</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{course.attendances.map((attendance) => {
									const statusInfo = attendanceStatusLabels[
										attendance.status
									] || {
										label: attendance.status,
										icon: AlertCircle,
										color: "text-gray-600",
									};
									const StatusIcon = statusInfo.icon;

									return (
										<TableRow key={attendance.id}>
											<TableCell>
												{new Date(attendance.date).toLocaleDateString("fr-FR")}
											</TableCell>
											<TableCell>
												{attendance.student?.firstName}{" "}
												{attendance.student?.lastName}
											</TableCell>
											<TableCell>
												<div
													className={`flex items-center gap-2 ${statusInfo.color}`}
												>
													<StatusIcon className="h-4 w-4" />
													<span>{statusInfo.label}</span>
												</div>
											</TableCell>
											<TableCell className="text-sm text-gray-600">
												{attendance.notes || "-"}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-12">
							<CheckCircle2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
							<p className="text-gray-500 font-medium">
								Aucune présence enregistrée
							</p>
							<p className="text-gray-400 text-sm mt-1">
								Les présences peuvent être enregistrées depuis la page de
								gestion des présences
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Excluded Dates Management */}
			<Card>
				<CardHeader>
					<CardTitle>Dates où le cours n'a pas eu lieu</CardTitle>
					<CardDescription>
						Définir les dates d'annulation ou de report du cours (vacances,
						jours fériés, etc.)
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{course.excludedDates && course.excludedDates.length > 0 ? (
							<div className="space-y-2">
								{course.excludedDates
									.sort((a: string, b: string) => new Date(a).getTime() - new Date(b).getTime())
									.map((date: string) => (
										<div
											key={date}
											className="flex items-center justify-between p-3 border rounded-lg"
										>
											<div className="flex items-center gap-2">
												<Calendar className="h-4 w-4 text-gray-400" />
												<span className="font-medium">
													{new Date(date).toLocaleDateString("fr-FR", {
														weekday: "long",
														year: "numeric",
														month: "long",
														day: "numeric",
													})}
												</span>
											</div>
											{!isArchived && (
												<Button
													size="sm"
													variant="ghost"
													onClick={() => removeExcludedDateMut.mutate({
														path: { courseId: id },
														body: { excludedDate: date },
													})}
													disabled={removeExcludedDateMut.isPending}
												>
													<X className="h-4 w-4" />
												</Button>
											)}
										</div>
									))}
							</div>
						) : (
							<div className="text-center py-8 text-gray-500">
								<Calendar className="mx-auto h-8 w-8 text-gray-400 mb-2" />
								<p className="text-sm">Aucune date d'exclusion définie</p>
							</div>
						)}

						{!isArchived && (
						<div className="pt-4 border-t">
							<Label htmlFor="new-excluded-date">
								Ajouter une date d'exclusion
							</Label>
							<div className="flex gap-2 mt-2">
								<Input
									id="new-excluded-date"
									type="date"
									value={newExcludedDate}
									onChange={(e) => setNewExcludedDate(e.target.value)}
								/>
								<Button
									onClick={() => {
										if (newExcludedDate) {
											addExcludedDateMut.mutate({
												path: { courseId: id },
												body: { excludedDate: newExcludedDate },	
											});
										}
									}}
									disabled={!newExcludedDate || addExcludedDateMut.isPending}
								>
									Ajouter
								</Button>
							</div>
							<p className="text-xs text-gray-500 mt-2">
								Ces dates seront exclues de la facturation et des exports Excel
							</p>
						</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Dialog pour archiver le cours */}
			<ArchiveCourseDialog
				open={archiveDialogOpen}
				onOpenChange={setArchiveDialogOpen}
				courseName={`${getSubjectLabel(course.subject)} - ${getLevelLabel(course.level)} (${getSchoolLabel(course.school)})`}
				activeEnrollmentCount={course.enrollments?.length ?? 0}
				isPending={archiveCourseMut.isPending}
				onConfirm={(archiveDate) =>
					archiveCourseMut.mutate({
						path: { id },
						body: { archiveDate: archiveDate || undefined },
					})
				}
			/>

			{/* Dialog pour désinscrire un élève */}
			<UnenrollDialog
				open={unenrollDialogOpen}
				onOpenChange={setUnenrollDialogOpen}
				studentName={selectedStudent ? `${selectedStudent.firstName} ${selectedStudent.lastName}` : ""}
				courseName={course ? `${getSubjectLabel(course.subject)} - ${getLevelLabel(course.level)}` : ""}
				isPending={unenrollStudentMut.isPending}
				isCancelPending={cancelUnenrollMut.isPending}
				existingUnenrollDate={existingUnenrollDate}
				onConfirm={handleUnenrollConfirm}
				onCancelUnenroll={existingUnenrollDate ? handleCancelUnenroll : undefined}
			/>

			{/* Dialog pour changer un élève de cours */}
			<Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Changer l'élève de cours</DialogTitle>
						<DialogDescription>
							Déplacer {selectedStudent?.firstName} {selectedStudent?.lastName}{" "}
							vers un autre cours
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div>
							<Label htmlFor="target-course">Cours de destination</Label>
							<Select value={targetCourseId} onValueChange={setTargetCourseId}>
								<SelectTrigger>
									<SelectValue placeholder="Sélectionner un cours" />
								</SelectTrigger>
								<SelectContent>
									{(allCourses || [])
										.filter((c) => c.id !== id)
										.map((c) => (
											<SelectItem key={c.id} value={c.id}>
												{getSubjectLabel(c.subject)} - {getLevelLabel(c.level)}{" "}
												({getSchoolLabel(c.school)})
											</SelectItem>
										))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setMoveDialogOpen(false)}>
							Annuler
						</Button>
						<Button
							onClick={handleMoveStudent}
							disabled={moveStudentMut.isPending}
						>
							{moveStudentMut.isPending ? "Déplacement..." : "Changer de cours"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
